import { useLayoutEffect, useState } from 'react';

/**
 * Промежуток между управлением и соседями снимается с самой строки: он задан классом разметки,
 * и своё число здесь молча разошлось бы с ней — сменивший класс промежутка сдвинул бы порог, по
 * которому итог решает забрать строку себе. Промежуток между наименованием и результатом уже
 * входит в замер. Без промежутка `column-gap` — `normal`, для расчёта это ноль.
 */
const getRowGap = (row: HTMLElement): number => {
  const gap = parseFloat(getComputedStyle(row).columnGap);
  return Number.isNaN(gap) ? 0 : gap;
};

/**
 * Запас в пикселях: измеренная сумма частей и ширина строки расходятся на доли пикселя
 * округлением, и без запаса итог уходил бы на свою строку, ещё помещаясь в общую.
 */
const FIT_TOLERANCE = 1;

/** Умещение итога и стадии проверки в одной строке */
export type TotalRowFit = {
  /** Строка с наименованием и результатом: её ширина и решает, помещается ли итог рядом со стадией */
  rowRef: (element: HTMLElement | null) => void;
  /** Слой замера: в нём наименование и результат стоят в одну строку */
  measureRef: (element: HTMLElement | null) => void;
  /** Управление строки итога: стадия проверки и кнопки */
  controlsRef: (element: HTMLElement | null) => void;
  /** Итог занял строку целиком — стадия проверки встала отдельной строкой над ней */
  isStacked: boolean;
};

/**
 * Умещение строки итога заключения: помещается ли итог рядом со стадией проверки или забирает
 * строку себе.
 *
 * Решает не ширина контейнера, а ширина, которой требует сам итог: набор значений строится
 * правилом расчёта, и его длина заранее не известна. Поэтому ширина итога в одну строку снимается
 * со скрытого слоя замера и складывается с шириной управления — ужиматься управлению некуда, а
 * итогу есть, и на своей строке он переносится по значениям. Замер повторяется на каждое изменение
 * ширины строки, состава итога и подписи стадии: величины снимаются независимо от действующей
 * раскладки, поэтому одна раскладка не вызывает другую.
 */
export const useTotalRowFit = (): TotalRowFit => {
  const [row, setRow] = useState<HTMLElement | null>(null);
  const [measure, setMeasure] = useState<HTMLElement | null>(null);
  const [controls, setControls] = useState<HTMLElement | null>(null);
  const [isStacked, setIsStacked] = useState(false);

  useLayoutEffect(() => {
    if (!row || !measure) return;

    const update = () => {
      const controlsWidth = controls ? controls.getBoundingClientRect().width + getRowGap(row) : 0;
      const needed = measure.getBoundingClientRect().width + controlsWidth;
      setIsStacked(needed > row.getBoundingClientRect().width + FIT_TOLERANCE);
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(row);
    observer.observe(measure);
    if (controls) observer.observe(controls);

    return () => observer.disconnect();
  }, [row, measure, controls]);

  return { rowRef: setRow, measureRef: setMeasure, controlsRef: setControls, isStacked };
};
